import React, { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import Loader from "../../../Shared/Components/Loader/Loader";
import "./PDFViewerScoped.css";
import "./PDFViewer.css";

// Set up PDF.js worker — use CDN matching the exact installed pdfjs-dist version
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Constants
const DEFAULT_ZOOM = 1.5;
const MAX_ZOOM = 2.0;
const MIN_ZOOM = 0.5;
const ZOOM_STEP = 0.2;
const CONTAINER_PADDING = 40;
const MAX_CONTAINER_WIDTH = 800;
const PDF_WIDTH_PADDING = 100;
const MAX_PDF_WIDTH = 600;
const SCROLL_TIMEOUT = 100;
const MIN_PDF_WIDTH = 200;

const PdfViewer = ({ pdfUrl, open, onClose, page }) => {
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(page || 1);
  const [scale, setScale] = useState(DEFAULT_ZOOM);
  const [containerWidth, setContainerWidth] = useState(MAX_CONTAINER_WIDTH);
  const containerRef = useRef(null);
  const pageRefs = useRef([]); // refs for each page

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(false);
    setCurrentPage(page || 1);
    setScale(DEFAULT_ZOOM);
    setContainerWidth(
      Math.min(window.innerWidth - CONTAINER_PADDING, MAX_CONTAINER_WIDTH)
    );

    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    }, SCROLL_TIMEOUT);
  }, [pdfUrl, open, page]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(
        Math.min(window.innerWidth - CONTAINER_PADDING, MAX_CONTAINER_WIDTH)
      );
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!open) return null;

  // Custom loading and error overlays
  const renderLoader = () => (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20">
      <Loader />
      <div className="mt-4 text-lg font-medium">Loading PDF...</div>
    </div>
  );

  const renderError = () => (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20">
      <div className="text-lg font-medium mb-2">Failed to load PDF.</div>
      <button
        className="flex items-center text-blue-600 font-semibold hover:underline"
        onClick={() => {
          setError(false);
          setLoading(true);
          setCurrentPage(1);
        }}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M4 4v5h5"
            stroke="#434DCF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M4.93 4.93A8 8 0 1 1 4 10"
            stroke="#434DCF"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        <span className="ml-2">Refresh</span>
      </button>
    </div>
  );

  // Handle document load success
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(false);
  };

  const onDocumentLoadError = (error) => {
    console.error("Error loading PDF:", error);
    setLoading(false);
    setError(true);
  };

  // Scroll when the desired page has finished rendering
  const onPageLoadSuccess = ({ pageNumber }) => {
    setLoading(false);
    if (pageNumber === page && pageRefs.current[page - 1]) {
      pageRefs.current[page - 1].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Zoom and scroll controls
  const zoomIn = () => {
    setScale((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  };

  const resetZoom = () => {
    setScale(DEFAULT_ZOOM);
  };

  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div
      className="pdf-viewer-modal fixed inset-0 z-50 w-full h-full bg-white flex flex-col"
      style={{ color: "var(--dark-neutral-text)" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white z-10">
        <div className="flex items-center">
          <img src="/Assets/Images/crossIcon.svg" alt="" aria-hidden="true" role="presentation" className="w-4 h-4 cursor-pointer" onClick={onClose} />
        </div>
      </div>

      {/* PDF Container */}
      <div className="flex-1 relative overflow-hidden">
        {loading && renderLoader()}
        {error && renderError()}
        <div
          ref={containerRef}
          className="w-full h-full overflow-y-auto relative"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={renderLoader()}
            error={renderError()}
          >
            {Array.from(new Array(numPages), (el, index) => (
              <div
                key={`page_${index + 1}`}
                ref={(el) => (pageRefs.current[index] = el)}
              >
                <Page
                  pageNumber={index + 1}
                  scale={scale}
                  onLoadSuccess={onPageLoadSuccess}
                  className="shadow-lg mx-auto mb-4"
                  width={Math.max(
                    MIN_PDF_WIDTH,
                    Math.min(containerWidth - PDF_WIDTH_PADDING, MAX_PDF_WIDTH)
                  )}
                />
              </div>
            ))}
          </Document>
        </div>
      </div>
    </div>
  );
};

export default PdfViewer;